/**
 * COS 直传上传工具函数
 * 用于将文件分片直接上传到腾讯云 COS
 */

import { IParallelUploadOptions, IUploadChunkOptions, IUploadedPart } from 'types/pipeline';

/**
 * 上传单个分片到 COS
 * @returns 返回 ETag，用于后续合并分片
 */
export const uploadChunkToCOS = ({
  presignedUrl,
  chunk,
  partNumber,
  signal,
  onProgress,
}: IUploadChunkOptions): Promise<IUploadedPart> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', presignedUrl, true);

    // 监听取消信号
    const abortHandler = () => {
      xhr.abort();
      reject(new Error('上传已取消'));
    };

    if (signal) {
      if (signal.aborted) {
        reject(new Error('上传已取消'));
        return;
      }
      signal.addEventListener('abort', abortHandler);
    }

    // 上传进度
    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          onProgress(event.loaded, event.total);
        }
      };
    }

    xhr.onload = () => {
      if (signal) {
        signal.removeEventListener('abort', abortHandler);
      }

      if (xhr.status >= 200 && xhr.status < 300) {
        // COS 返回的 ETag 在响应头中，需要去掉引号
        const etag = (xhr.getResponseHeader('ETag') || '').replace(/"/g, '');
        if (!etag) {
          reject(new Error(`分片 ${partNumber} 上传成功但未返回 ETag`));
          return;
        }
        resolve({ partNumber, etag });
      } else {
        reject(new Error(`分片 ${partNumber} 上传失败: HTTP ${xhr.status}`));
      }
    };

    xhr.onerror = () => {
      if (signal) {
        signal.removeEventListener('abort', abortHandler);
      }
      reject(new Error(`分片 ${partNumber} 上传失败: 网络错误`));
    };

    xhr.ontimeout = () => {
      if (signal) {
        signal.removeEventListener('abort', abortHandler);
      }
      reject(new Error(`分片 ${partNumber} 上传超时`));
    };

    // 设置超时时间（5分钟）
    xhr.timeout = 5 * 60 * 1000;

    xhr.send(chunk);
  });
};

export const uploadChunksInParallel = async ({
  file,
  chunkSize,
  presignedUrls,
  signal,
  parallelLimit = 3,
  onProgress,
}: IParallelUploadOptions): Promise<IUploadedPart[]> => {
  const totalChunks = presignedUrls.length;
  const uploadedParts: IUploadedPart[] = [];
  let uploadedCount = 0;

  const checkAborted = () => {
    if (signal?.aborted) {
      throw new Error('上传已取消');
    }
  };

  // 按批次并行上传
  for (let batchStart = 0; batchStart < totalChunks; batchStart += parallelLimit) {
    checkAborted();

    const batchEnd = Math.min(batchStart + parallelLimit, totalChunks);
    const uploadPromises: Promise<IUploadedPart>[] = [];

    for (let i = batchStart; i < batchEnd; i++) {
      const urlItem = presignedUrls[i];
      const partNumber = urlItem.partNumber;
      const presignedUrl = urlItem.url;

      // 根据 partNumber 计算分片范围
      const start = (partNumber - 1) * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);

      const uploadPromise = uploadChunkToCOS({
        presignedUrl,
        chunk,
        partNumber,
        signal,
      }).then((part) => {
        uploadedCount++;
        if (onProgress) {
          onProgress(uploadedCount, totalChunks);
        }
        return part;
      });

      uploadPromises.push(uploadPromise);
    }

    const batchResults = await Promise.all(uploadPromises);
    uploadedParts.push(...batchResults);
  }

  // 按 partNumber 排序
  return uploadedParts.sort((a, b) => a.partNumber - b.partNumber);
};
